import { useEffect, useState } from "react";
import { useLogoSizes, type LogoSizes } from "@/contexts/LogoSizeContext";
import { useUserRole } from "@/hooks/useUserRole";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Settings2, X, RotateCcw } from "lucide-react";

/**
 * Floating dev panel for live-tweaking logo sizes across breakpoints.
 * Only mounted in dev (import.meta.env.DEV) so it never ships to prod.
 */
const ROWS: { key: keyof LogoSizes; label: string; min: number; max: number }[] = [
  { key: "navMobile", label: "Nav (mobile, <640px)", min: 24, max: 200 },
  { key: "navSm", label: "Nav (sm, 640-767px)", min: 24, max: 220 },
  { key: "navTablet", label: "Nav (tablet, 768-1023px)", min: 24, max: 240 },
  { key: "navDesktop", label: "Nav (desktop, ≥1024px)", min: 24, max: 260 },
  { key: "footerMobile", label: "Footer (mobile, <768px)", min: 24, max: 200 },
  { key: "footer", label: "Footer (desktop, ≥768px)", min: 24, max: 220 },
  { key: "taglineNav", label: "Tagline (nav) font-size", min: 8, max: 28 },
  { key: "taglineFooter", label: "Tagline (footer) font-size", min: 8, max: 28 },
];

export const LogoSizerPanel = () => {
  const { role, loading: roleLoading } = useUserRole();
  const { sizes, setSize, reset } = useLogoSizes();
  const [open, setOpen] = useState(false);
  const [vw, setVw] = useState(typeof window !== "undefined" ? window.innerWidth : 0);

  useEffect(() => {
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const activeNavKey: keyof LogoSizes =
    vw >= 1024 ? "navDesktop" : vw >= 768 ? "navTablet" : vw >= 640 ? "navSm" : "navMobile";
  if (roleLoading || role !== "admin") return null;

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Open logo sizer"
        className="fixed bottom-4 right-4 z-[9999] inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg hover:opacity-90"
      >
        <Settings2 className="h-5 w-5" />
      </button>
    );
  }

  return (
    <div
      role="dialog"
      aria-label="Logo sizer"
      className="fixed bottom-4 right-4 z-[9999] w-[320px] rounded-xl border border-border bg-popover/95 backdrop-blur p-4 shadow-2xl"
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-sm font-semibold">Logo sizer</p>
          <p className="text-[10px] text-muted-foreground">
            Viewport: {vw}px - active nav: {activeNavKey}
          </p>
        </div>
        <div className="flex items-center gap-1">
          <Button size="icon" variant="ghost" onClick={reset} aria-label="Reset">
            <RotateCcw className="h-4 w-4" />
          </Button>
          <Button size="icon" variant="ghost" onClick={() => setOpen(false)} aria-label="Close">
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        {ROWS.map((row) => {
          const value = sizes[row.key];
          const isActive = row.key === activeNavKey;
          return (
            <div key={row.key}>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className={isActive ? "font-semibold text-primary" : "text-foreground"}>
                  {row.label}
                </span>
                <span className="tabular-nums text-muted-foreground">{value}px</span>
              </div>
              <Slider
                value={[value]}
                min={row.min}
                max={row.max}
                step={1}
                onValueChange={([v]) => setSize(row.key, v)}
              />
            </div>
          );
        })}
      </div>

      <p className="mt-3 text-[10px] text-muted-foreground">
        Saved to localStorage. Hidden in production builds.
      </p>
    </div>
  );
};

export default LogoSizerPanel;
